<script lang="ts">
const defaultSelectedState = {
  quote: null as null | Quote,
  carrierGroup: null as null | Carrier,
  issuingCompany: null as null | IssuingCompany,
  wholesalerGroup: null as null | WholesalerGroup,
  wholesaler: null as null | Wholesaler,
  underwriter: '',
  admitted: false,
  incumbent: false,
  bound: false,
  responseStatus: null as null | QuoteRefDataType,
  responseDate: '',
  declinedReason: null as null | QuoteRefDataType,
  layerId: -1,
  limit: null as null | number | string,
  surplusLinesTax: null as null | number | string,
  quotaShareTotal: null as null | number | string,
  retention: null as null | number | string,
  attachmentPoint: null as null | number | string,
  premium: null as null | number | string,
  commissionPercent: null as null | number | string,
  commissionAmount: null as null | number | string,
  quoteItemValues: [] as { value?: string, dirty: boolean, valid?: boolean, error?:string }[]
};

interface QuoteItemTypeDisplay extends QuoteItemType {
  index: number;
  selector: string;
}

function isNumberOrEmpty(val: null | string | number): boolean {
  if (val === null || typeof val === 'number' || val === '') {
    return true;
  }
  return !isNaN(parseInt(val));
}

function parseIntOrNull(val: unknown): number | null {
  return parseIntOrDefault(val, null);
}

function parseFloatOrNull(val: unknown): number | null {
  return parseFloatOrDefault(val, null);
}

export default {
  name: 'QuoteAddEditDialog',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Carrier, IssuingCompany } from '@/types/Carrier';
import { Quote, QuoteItem, QuoteItemType, QuoteRefDataType } from '@/types/Quote';
import { carrierService, wholesalerGroupService } from '@/services/api';
import NumberInput from '@/components/v2/NumberInput.vue';
import { parseFloatOrDefault, parseIntOrDefault } from '@/utilities/Number';
import { Wholesaler, WholesalerGroup } from '@/types/WholesalerGroup';
import Skeleton from '@/components/v2/Skeleton.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';
import TextField from '@/components/v2/TextField.vue';
import Combobox from '@/components/v2/Combobox.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import TextArea from '@/components/ui/TextArea.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';

const props = withDefaults(defineProps<{
  showDialog?: boolean
  title: string
  saveButtonTitle: string
  edit?: boolean
  quote?: Quote | null
  carriers: Carrier[]
  wholesalerGroups: WholesalerGroup[]
  quoteResponseTypes: QuoteRefDataType[]
  quoteDeclineReasonTypes: QuoteRefDataType[]
  quoteItemTypes: QuoteItemType[]
}>(), {
    showDialog: false,
    edit: false,
  });

const emit = defineEmits<{
  cancel: []
  confirm: [Quote]
}>();

const loading = ref(false);
const selected = ref({} as typeof defaultSelectedState);
const issuingCompanies = ref([] as IssuingCompany[]);
const issuingCompaniesLoading = ref(false);
const wholesalers = ref([] as Wholesaler[]);
const wholesalersLoading = ref(false);
const isEditingCommissionAmount = ref(false);

const displayDialog = computed<boolean>(() => {
  return props.showDialog;
});

const selectedCarrierGroup = computed<Carrier | null>({
  get(): Carrier | null {
    return selected.value.carrierGroup;
  },
  set(val: Carrier | null): void {
    selected.value.carrierGroup = typeof val === 'string' ? null : val;
    selected.value.issuingCompany = null;
  }
});

const selectedWholesalerGroup = computed<WholesalerGroup | null>({
  get(): WholesalerGroup | null {
    return selected.value.wholesalerGroup;
  },
  set(val: WholesalerGroup | null): void {
    selected.value.wholesalerGroup = typeof val === 'string' ? null : val;
    selected.value.wholesaler = null;
  }
});

const carrierGroupOptions = computed(() =>
  props.carriers.map((c) => ({ label: c.name, value: String(c.ID) }))
);
const issuingCompanyOptions = computed(() =>
  issuingCompanies.value.map((c) => ({
    label: c.issuingCompanyName,
    value: String(c.issuingCompanyCode ?? ''),
  }))
);
const wholesalerGroupOptions = computed(() =>
  props.wholesalerGroups.map((g) => ({ label: g.name, value: String(g.id) }))
);
const wholesalerOptions = computed(() =>
  wholesalers.value.map((w) => ({ label: w.name, value: String(w.id) }))
);
const responseStatusOptions = computed(() =>
  (props.quoteResponseTypes || []).map((t) => ({ label: t.name, value: String(t.id) }))
);
const declineReasonOptions = computed(() =>
  (props.quoteDeclineReasonTypes || []).map((t) => ({ label: t.name, value: String(t.id) }))
);

const quoteItemTypesDisplay = computed<QuoteItemTypeDisplay[][]>(() => {
  if (!props.quoteItemTypes) {
    return [];
  }

  // 'text' types get a full width textarea, others get a half width input component
  let [rows, currRow] = [[] as QuoteItemTypeDisplay[][], [] as QuoteItemTypeDisplay[]];

  const selector = (t: QuoteItemType): string => `quote-${t.name.toLowerCase().replaceAll(' ', '-')}`;

  props.quoteItemTypes.forEach((t, index) => {
    switch (t.optionType) {
      case 'text':
        if (currRow.length > 0) { // if a half width component was previously processed
          rows.push([...currRow]);
          currRow = [];
        }
        rows.push([{ ...t, index, selector: selector(t) }]);
        break;

      default:
        if (currRow.length === 2) {
          rows.push([...currRow]);
          currRow = [];
        }
        currRow.push({ ...t, index, selector: selector(t) });

        // if the last element is half width, then push a 'blank' into the other slot
        if (index === (props.quoteItemTypes || []).length - 1 && currRow.length < 2) {
          currRow.push({...t, index, optionType: 'blank', selector: 'blank' });
        }
    }
  });
  rows.push([...currRow]);
  return rows;
});

const selectedPremium = computed({
  get(): null | string | number {
    return selected.value.premium;
  },
  set(val: null | string | number) {
    selected.value.premium = val;

    const [commPct, premium] = [parseFloatOrNull(selected.value.commissionPercent), parseIntOrNull(val)];

    if (premium && commPct) {
      selected.value.commissionAmount = premium * (commPct / 100);
      return;
    }
    if (commPct === 0) {
      selected.value.commissionAmount = 0;
    }
  },
});

const selectedCommissionPercent = computed({
  get(): null | string | number {
    return selected.value.commissionPercent;
  },
  set(val: null | string | number) {
    selected.value.commissionPercent = val;

    const [commAmount, premium] = [parseFloatOrNull(selected.value.commissionAmount), parseIntOrNull(selected.value.premium)];

    if (commAmount && premium && premium > 0) {
      selected.value.commissionPercent = (commAmount / premium) * 100;
      return;
    }
    if (commAmount === 0) {
      selected.value.commissionPercent = 0;
    }
  },
});

const selectedCommissionAmount = computed({
  get(): null | string | number {
    return selected.value.commissionAmount;
  },
  set(val: null | string | number) {
    selected.value.commissionAmount = val;

    const [commAmount, premium] = [parseFloatOrNull(val), parseIntOrNull(selected.value.premium)];

    if (commAmount && premium && premium > 0) {
      selected.value.commissionPercent = (commAmount / premium) * 100;
      return;
    }
    if (commAmount === 0) {
      selected.value.commissionPercent = 0;
    }
  },
});

watch(()=> props.quote, (quote) => {
  if (quote === null || loading.value) {
    return;
  }

  loading.value = true;

  const carriersMap = props.carriers.reduce((acc, c) => {
    const carrierId = c.ID;
    if (isNaN(carrierId)) {
      return acc;
    }
    acc.set(carrierId, c);
    return acc;
  }, new Map<number, Carrier>());

  const quoteResponseTypesMap = props.quoteResponseTypes.reduce((acc, t) => {
    acc.set(t.id, t);
    return acc;
  }, new Map<number, QuoteRefDataType>());

  const quoteDeclineReasonTypesMap = props.quoteDeclineReasonTypes.reduce((acc, t) => {
    acc.set(t.id, t);
    return acc;
  }, new Map<number, QuoteRefDataType>());


  if (quote) {
    selected.value = {
       ...defaultSelectedState,
      quote,
      carrierGroup: quote.insurerGroupId ? carriersMap.get(quote.insurerGroupId) ?? null : null,
      issuingCompany: null,
      wholesalerGroup: quote?.wholesalerGroupId ? props.wholesalerGroups.find(g => g.id === quote.wholesalerGroupId) ?? null : null,
      underwriter: quote.underwriter ?? '',
      admitted: quote.admitted ?? false,
      incumbent: quote.incumbent ?? false,
      bound: quote.bound ?? false,
      responseStatus: quote.responseStatusId ? quoteResponseTypesMap.get(quote.responseStatusId) ?? null : null,
      responseDate: quote.responseDate ? quote.responseDate.split('T')[0] : '',
      declinedReason: quote.declinedReasonId ? quoteDeclineReasonTypesMap.get(quote.declinedReasonId) ?? null : null,
      layerId: quote.layerId ?? -1,
      limit: quote.aggregateLimit ?? null,
      quotaShareTotal: quote.quotashareTotal ?? null,
      retention: quote.retention ?? null,
      attachmentPoint: quote.attachmentPoint ?? null,
      premium: quote.premium ?? null,
      commissionPercent: quote.commissionPercent ? quote.commissionPercent * 100 : null,
      commissionAmount: quote.commissionPercent && quote.premium ? quote.commissionPercent * quote.premium : null,
      surplusLinesTax: quote.surplusLinesTax ?? null,
      quoteItemValues: props.quoteItemTypes ? props.quoteItemTypes.map(t => ({value: quote.quoteItems?.find(i => i.quoteItemTypeId === t.quoteItemTypeId)?.value , dirty: false})) : [],
    };
  } else {
    selected.value = { ...defaultSelectedState}
  }

  if (props.quoteItemTypes) {
    selected.value.quoteItemValues = props.quoteItemTypes.map(t => {
      const value = quote?.quoteItems?.find(i => i.quoteItemTypeId === t.quoteItemTypeId);

      return {
        value: value?.value,
        dirty: false,
        valid: !!value
      };
    });
  }

  (async () => {
    if (selected.value.carrierGroup) {
      issuingCompaniesLoading.value = true;
      issuingCompanies.value = await carrierService.issuingCompanies(selected.value.carrierGroup.ID);
      issuingCompaniesLoading.value = false;
    }

    if (selected.value.wholesalerGroup) {
      wholesalersLoading.value = true;
      wholesalers.value = await wholesalerGroupService.wholesalers(selected.value.wholesalerGroup.id);
      wholesalersLoading.value = false;
    }

    if (quote?.issuingCompanyId) {
      selected.value.issuingCompany = issuingCompanies.value.find(company => company.issuingCompanyId === quote.issuingCompanyId) ?? null;
    }

    if (quote?.wholesalerGroupId) {
      selected.value.wholesaler = wholesalers.value.find(w => w.id === quote.wholesalerId) ?? null;
    }
  })();

  loading.value = false;
});

function carrierSelected(val: Carrier) {
  if (!val.ID) {
    return;
  }

  issuingCompanies.value = [];
  selected.value.issuingCompany = null;
  (async () => {
    issuingCompaniesLoading.value = true;
    issuingCompanies.value = await carrierService.issuingCompanies(val.ID);
    issuingCompaniesLoading.value = false;
  })();
}

function wholesalerGroupSelected(val: WholesalerGroup) {
  if (!val.id) {
    return;
  }

  wholesalers.value = [];
  selected.value.wholesaler = null;
  (async () => {
    wholesalersLoading.value = true;
    wholesalers.value = await wholesalerGroupService.wholesalers(val.id);
    wholesalersLoading.value = false;
  })();
}

function quoteItemPrefix(item: QuoteItemType) {
  switch (item.optionType) {
    case 'percent':
      return '%';
    case 'currency':
      return '$';
  }
  return '';
}

function dirtyQuoteValue(i: number) {
  selected.value.quoteItemValues[i].dirty = true;
}

function validateQuoteValues() {
  selected.value.quoteItemValues = selected.value.quoteItemValues.map((v, i) => {
    if (!props.quoteItemTypes || !v.dirty) {
      return v;
    }
    // only perform validation on dirtied inputs
    const type = props.quoteItemTypes[i];

    if (!type.validatorRegex || !v.value) {
      return { ...v, valid: true };
    }

    try {
      v.valid = new RegExp(type.validatorRegex).test(v.value);
      v.error = v.valid ? undefined : type.validatorMessage || 'Invalid';
    } catch (e) {
      console.error(`Invalid regex for ${type.quoteItemTypeId}`, e);
    }
    return v;
  });
}

function editCommissionAmount(): void {
  isEditingCommissionAmount.value = !isEditingCommissionAmount.value;
}

function onCarrierGroupChange(val: string) {
  const c = props.carriers.find((x) => String(x.ID) === val) ?? null;
  selected.value.carrierGroup = c;
  selected.value.issuingCompany = null;
  if (c) carrierSelected(c);
}

function onWholesalerGroupChange(val: string) {
  const g = props.wholesalerGroups.find((x) => String(x.id) === val) ?? null;
  selected.value.wholesalerGroup = g;
  selected.value.wholesaler = null;
  if (g) wholesalerGroupSelected(g);
}

function isConfirmDisabled() {
  return !isQuoteValuesValid()
      || (selected.value.carrierGroup === null && selected.value.wholesalerGroup === null)
      || !isNumberOrEmpty(selected.value.retention)
      || !isNumberOrEmpty(selected.value.limit)
      || !isNumberOrEmpty(selected.value.quotaShareTotal)
      || !isNumberOrEmpty(selected.value.attachmentPoint)
      || !isNumberOrEmpty(selected.value.premium)
      || !isNumberOrEmpty(selected.value.commissionAmount)
      || !isNumberOrEmpty(selected.value.commissionPercent);
}

function isQuoteValuesValid() {
  if (selected.value.quoteItemValues) {
    return selected.value.quoteItemValues.filter(v => v.dirty && v.valid === false).length === 0;
  }
  return true;
}

function saveQuote() {
  const quote = (props.edit && selected.value.quote) ? selected.value.quote : {} as Quote;

  quote.insurerGroupId = parseIntOrNull(selected.value.carrierGroup?.ID);
  quote.insurerGroup = selected.value.carrierGroup?.name;
  quote.issuingCompanyId = selected.value.issuingCompany?.issuingCompanyId;
  quote.wholesalerGroupId = selected.value.wholesalerGroup?.id;
  quote.wholesalerGroup = selected.value.wholesalerGroup?.name;
  quote.wholesalerId = selected.value.wholesaler?.id;
  quote.aggregateLimit = parseIntOrNull(selected.value.limit);
  quote.surplusLinesTax = parseIntOrNull(selected.value.surplusLinesTax);
  quote.quotashareTotal = parseFloatOrNull(selected.value.quotaShareTotal);
  quote.retention = parseIntOrNull(selected.value.retention);
  quote.attachmentPoint = parseIntOrNull(selected.value.attachmentPoint);
  quote.premium = parseIntOrNull(selected.value.premium);
  // Convert date string (YYYY-MM-DD) to ISO datetime format for backend
  quote.responseDate = selected.value.responseDate 
    ? new Date(selected.value.responseDate + 'T00:00:00').toISOString()
    : undefined;
  quote.responseStatusId = selected.value.responseStatus?.id;
  quote.responseStatus = selected.value.responseStatus?.name;
  quote.underwriter = selected.value.underwriter.trim();
  quote.declinedReasonId = selected.value.declinedReason?.id;
  quote.declinedReason = selected.value.declinedReason ? '' + selected.value.declinedReason?.name : undefined;
  quote.admitted = selected.value.admitted;
  quote.incumbent = selected.value.incumbent;
  quote.bound = selected.value.bound;

  const commission = parseFloatOrNull(selected.value.commissionPercent);
  quote.commissionPercent = commission === null ? null : commission / 100;

  if (props.quoteItemTypes) {
    const types = props.quoteItemTypes;

    quote.quoteItems = selected.value.quoteItemValues.reduce((items, val, i) => {
      if (val.valid) {
        items.push({
          quoteId: quote.quoteId || -1,
          quoteItemTypeId: types[i].quoteItemTypeId,
          value: `${val.value}`
        });
      }
      return items;
    }, [] as QuoteItem[]);
  }

  quote.displayKey = Math.random(); // forces a render (used as a value for displayKey prop on QuoteCard)

  emit('confirm', quote);
}
</script>

<template>
  <Skeleton v-if="loading" />
  <AppModal
    v-else
    :show="displayDialog"
    :title="title"
    cancel-button-label="Cancel"
    :action-button-label="saveButtonTitle"
    :handle-cancel="() => emit('cancel')"
    :handle-action="saveQuote"
    :action-disabled="isConfirmDisabled()"
    size="lg"
    show-overflow
  >
    <template #content>
      <div class="space-y-4 overflow-y-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="form-control w-full">
            <label class="label"><span class="label-text">* Carrier Group</span></label>
            <Combobox
              data-testid="carrier-group"
              :model-value="selectedCarrierGroup ? String(selectedCarrierGroup.ID) : ''"
              :options="carrierGroupOptions"
              @update:model-value="onCarrierGroupChange"
            />
          </div>
          <div class="form-control w-full">
            <label class="label"><span class="label-text">Issuing Company</span></label>
            <Combobox
              data-test="issuing-company"
              :model-value="selected.issuingCompany ? String(selected.issuingCompany.issuingCompanyCode ?? '') : ''"
              :options="issuingCompanyOptions"
              :disabled="issuingCompanies.length === 0"
              @update:model-value="(val: string) => { selected.issuingCompany = issuingCompanies.find((x) => String(x.issuingCompanyCode ?? '') === val) ?? null; }"
            />
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div class="form-control w-full">
            <label class="label"><span class="label-text">* Wholesaler Group</span></label>
            <Combobox
              data-testid="wholesaler-group"
              :model-value="selectedWholesalerGroup ? String(selectedWholesalerGroup.id) : ''"
              :options="wholesalerGroupOptions"
              @update:model-value="onWholesalerGroupChange"
            />
          </div>
          <div class="form-control w-full">
            <label class="label"><span class="label-text">Wholesaler</span></label>
            <Combobox
              data-test="wholesaler"
              :model-value="selected.wholesaler ? String(selected.wholesaler.id) : ''"
              :options="wholesalerOptions"
              :disabled="wholesalers.length === 0"
              @update:model-value="(val: string) => { selected.wholesaler = wholesalers.find((x) => String(x.id) === val) ?? null; }"
            />
          </div>
        </div>
        <div class="text-center text-sm pb-2">
          * Carrier Group or Wholesaler Group is required
        </div>
        <div class="checkbox-options flex justify-evenly">
          <Checkbox v-model="selected.admitted" label="Admitted" data-test="admitted" />
          <Checkbox v-model="selected.incumbent" label="Incumbent" data-test="incumbent" />
          <Checkbox v-model="selected.bound" label="Bound" data-test="bound" />
        </div>
        <TextField
          v-model="selected.underwriter"
          label="Underwriter"
          data-test="underwriter"
        />
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="form-control w-full">
            <label class="label"><span class="label-text">Response Status</span></label>
            <Combobox
              data-testid="response-status"
              :model-value="selected.responseStatus ? String(selected.responseStatus.id) : ''"
              :options="responseStatusOptions"
              @update:model-value="(val: string) => { selected.responseStatus = (props.quoteResponseTypes || []).find((x) => String(x.id) === val) ?? null; }"
            />
          </div>
          <DatePickerInput
            v-model="selected.responseDate"
            outlined
            label="Response Date"
            data-testid="quote-response-date"
          />
        </div>
        <div
          v-if="selected.responseStatus != null && selected.responseStatus.name === 'Declined'"
          class="form-control w-full mb-6"
        >
          <label class="label"><span class="label-text">Reason Declined</span></label>
          <Combobox
            :model-value="selected.declinedReason ? String(selected.declinedReason.id) : ''"
            :options="declineReasonOptions"
            @update:model-value="(val: string) => { selected.declinedReason = (props.quoteDeclineReasonTypes || []).find((x) => String(x.id) === val) ?? null; }"
          />
        </div>

        <template v-if="selected.responseStatus != null">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput
              label="Limit Value / Aggregate Limit"
              prefix="$"
              data-testid="quote-limit"
              v-model="selected.limit"
              :error="!isNumberOrEmpty(selected.limit)"
              :error-message="!isNumberOrEmpty(selected.limit) ? 'Number is required.' : undefined"
            />
            <NumberInput
              label="Total Layer Limit"
              prefix="$"
              data-test="quote-quotashare"
              v-model="selected.quotaShareTotal"
              :error="!isNumberOrEmpty(selected.quotaShareTotal)"
              :error-message="!isNumberOrEmpty(selected.quotaShareTotal) ? 'Number is required.' : undefined"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput
              label="Retention"
              prefix="$"
              data-test="quote-retention"
              v-model="selected.retention"
              :error="!isNumberOrEmpty(selected.retention)"
              :error-message="!isNumberOrEmpty(selected.retention) ? 'Number is required.' : undefined"
            />
            <NumberInput
              label="Attachment Point Value"
              prefix="$"
              data-test="quote-attachmentpoint"
              v-model="selected.attachmentPoint"
              :error="!isNumberOrEmpty(selected.attachmentPoint)"
              :error-message="!isNumberOrEmpty(selected.attachmentPoint) ? 'Number is required.' : undefined"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput
              label="Premium"
              data-test="quote-premium"
              prefix="$"
              v-model="selectedPremium"
              :error="!isNumberOrEmpty(selected.premium)"
              :error-message="!isNumberOrEmpty(selected.premium) ? 'Number is required.' : undefined"
            />
            <div class="grid grid-cols-2 gap-4">
              <TextField
                :model-value="selectedCommissionPercent ?? ''"
                label="Comm. %"
                type="number"
                data-test="quote-commission-percent"
                :error="!isNumberOrEmpty(selectedCommissionPercent)"
                :error-message="!isNumberOrEmpty(selectedCommissionPercent) ? 'Number is required.' : undefined"
                @update:model-value="(v) => { selected.commissionPercent = (v === '' || v === undefined) ? null : (typeof v === 'number' ? v : Number(v)); }"
              />
              <TextField
                :model-value="selectedCommissionAmount ?? ''"
                label="Comm. $"
                type="number"
                data-test="quote-commission-amount"
                :error="!isNumberOrEmpty(selectedCommissionAmount)"
                :error-message="!isNumberOrEmpty(selectedCommissionAmount) ? 'Number is required.' : undefined"
                @update:model-value="(v) => { selected.commissionAmount = (v === '' || v === undefined) ? null : (typeof v === 'number' ? v : Number(v)); }"
                @focus="editCommissionAmount"
                @blur="editCommissionAmount"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput
              label="Surplus Lines Tax"
              data-test="quote-surplus-lines-tax"
              prefix="$"
              v-model="selected.surplusLinesTax"
              :error="!isNumberOrEmpty(selected.surplusLinesTax)"
              :error-message="!isNumberOrEmpty(selected.surplusLinesTax) ? 'Number is required.' : undefined"
            />
            <div />
          </div>
          <template v-if="quoteItemTypes">
            <hr class="border-0 h-px bg-gray-200" />
            <br>
          </template>
          <div v-for="(row, i) in quoteItemTypesDisplay" :key="i" class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <template v-for="(option, j) in row" :key="j">
              <div v-if="option.optionType === 'blank'" />
              <NumberInput
                v-else-if="['currency', 'percent', 'decimal'].indexOf(option.optionType) > -1"
                :prefix="quoteItemPrefix(option)"
                :label="option.name"
                :data-test="option.selector"
                :model-value="selected.quoteItemValues[option.index].value"
                :error="!!selected.quoteItemValues[option.index].error"
                :error-message="selected.quoteItemValues[option.index].error"
                @update:model-value="(v) => { selected.quoteItemValues[option.index].value = v === null || v === undefined ? undefined : String(v); dirtyQuoteValue(option.index); }"
                @blur="validateQuoteValues"
              />
              <TextField
                v-else-if="option.optionType !== 'text'"
                :label="option.name"
                :data-test="option.selector"
                v-model="selected.quoteItemValues[option.index].value"
                :error="!!selected.quoteItemValues[option.index].error"
                :error-message="selected.quoteItemValues[option.index].error"
                @blur="() => { dirtyQuoteValue(option.index); validateQuoteValues(); }"
              />
              <div v-else-if="option.optionType === 'text'" class="form-control w-full">
                <label class="label"><span class="label-text">{{ option.name }}</span></label>
                <TextArea
                  :data-test="option.selector"
                  v-model="selected.quoteItemValues[option.index].value"
                  PermanentBorder
                  @blur="() => { dirtyQuoteValue(option.index); validateQuoteValues(); }"
                />
                <label v-if="selected.quoteItemValues[option.index].error" class="label">
                  <span class="label-text-alt text-error">{{ selected.quoteItemValues[option.index].error }}</span>
                </label>
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
    <template #actions>
      <AppButton data-testid="cancel-action" @click="() => emit('cancel')">Cancel</AppButton>
      <AppButton
        primary
        data-testid="confirm-save"
        :disabled="isConfirmDisabled()"
        @click="saveQuote()"
      >
        {{ saveButtonTitle }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.checkbox-options {
  display: flex;
  justify-content: space-evenly;
}
</style>
